/**
 * Consultas de la app del juez.
 *
 * Todo lo que el juez necesita para trabajar un heat se cachea en
 * localStorage al cargarlo. Si el wifi se cae a mitad de la competencia,
 * la tablet sigue mostrando el heat y aceptando scores: la lectura sale
 * de la cache y la escritura se va al outbox.
 */

import type { ScoreStatus, ScoreType, WorkoutStatus } from '@timecap/core';
import type { WodSegment } from '@timecap/core';

import { supabase } from './supabase.js';

export interface EventSummary {
  id: string;
  name: string;
  slug: string;
  status: string;
  role: string;
  /**
   * Cuando pierde el acceso. null = no caduca (owner y admin).
   *
   * is_event_staff() ya lo comprueba, asi que pasada esa fecha la base
   * rechaza todo. Pero la lista se lee de event_staff con una politica
   * que NO mira expires_at, asi que el evento seguia apareciendo: el
   * juez lo abria y nada funcionaba, sin explicacion.
   */
  expires_at: string | null;
}

export interface WorkoutSummary {
  id: string;
  event_id: string;
  name: string;
  slug: string;
  sort_order: number;
  /** El WOD escrito. Sin esto el juez no sabe que esta juzgando. */
  description: string | null;
  score_type: ScoreType;
  time_cap_seconds: number | null;
  rep_target: number | null;
  weight: number;
  tiebreak_enabled: boolean;
  tiebreak_label: string | null;
  status: WorkoutStatus;
}

export interface HeatSummary {
  id: string;
  name: string;
  status: string;
  scheduled_at: string | null;
  sort_order: number;
  started_at: string | null;
}

export interface LaneRow {
  id: string;
  lane_number: number;
  registration_id: string;
  display_name: string;
  bib_number: number | null;
  division_id: string;
  box_name: string | null;
  judge_id: string | null;
  /** Este carril es mio. */
  mine: boolean;
}

export interface ScoreRow {
  id: string;
  workout_id: string;
  registration_id: string;
  status: ScoreStatus;
  time_seconds: number | null;
  reps: number | null;
  load_kg: number | null;
  distance_m: number | null;
  capped: boolean;
  tiebreak_seconds: number | null;
  notes: string | null;
  /** Donde se quedo, si el juez uso la lista de movimientos. */
  segments_done: number | null;
  partial_reps: number | null;
}

// ---------------------------------------------------------------------
// Cache de lectura
// ---------------------------------------------------------------------
const CACHE_PREFIX = 'timecap.cache.';

function readCache<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(CACHE_PREFIX + key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function writeCache(key: string, value: unknown): void {
  try {
    localStorage.setItem(CACHE_PREFIX + key, JSON.stringify(value));
  } catch {
    // Cuota llena: no es motivo para romper la captura.
  }
}

/** Intenta la red; si falla, cae a lo ultimo que se vio. */
async function withCache<T>(key: string, load: () => Promise<T>): Promise<T> {
  try {
    const fresh = await load();
    writeCache(key, fresh);
    return fresh;
  } catch (error) {
    const cached = readCache<T>(key);
    if (cached) return cached;
    throw error;
  }
}

// ---------------------------------------------------------------------
// Consultas
// ---------------------------------------------------------------------
/**
 * Mi id, para filtrar `event_staff` a mano.
 *
 * La politica staff_select_own es `user_id = auth.uid() OR
 * is_event_admin(event_id)`: un admin ve las filas de TODO su equipo,
 * que es lo que necesita la pantalla de Equipo. Cualquier consulta que
 * quiera decir "lo mio" tiene que filtrar ella.
 */
async function miId(): Promise<string | null> {
  const { data } = await supabase.auth.getUser();
  return data.user?.id ?? null;
}

/**
 * Los movimientos del WOD, para la lista del juez.
 *
 * Se cachean como el resto: en el pabellon la red se cae, y sin esta
 * lista el juez no puede marcar donde se quedo nadie.
 */
export async function fetchSegments(workoutId: string): Promise<WodSegment[]> {
  return withCache(`segments:${workoutId}`, async () => {
    const { data, error } = await supabase
      .from('workout_segments')
      .select('sort_order, movement, reps, load_kg')
      .eq('workout_id', workoutId)
      .order('sort_order');
    if (error) throw error;
    return (data ?? []) as WodSegment[];
  });
}

export async function fetchMyEvents(): Promise<EventSummary[]> {
  return withCache('events', async () => {
    const uid = await miId();
    if (!uid) return [];

    // Sin el filtro, un organizador veia su competencia repetida una vez
    // por cada juez de su equipo, cada copia con el rol de otro.
    const { data, error } = await supabase
      .from('event_staff')
      .select('role, expires_at, events!inner(id, name, slug, status)')
      .eq('user_id', uid)
      .order('created_at', { ascending: false });
    if (error) throw error;

    return (data ?? []).map((row: any) => ({
      id: row.events.id,
      name: row.events.name,
      slug: row.events.slug,
      status: row.events.status,
      role: row.role,
      expires_at: row.expires_at ?? null,
    }));
  });
}

export async function fetchWorkouts(eventId: string): Promise<WorkoutSummary[]> {
  return withCache(`workouts.${eventId}`, async () => {
    const { data, error } = await supabase
      .from('workouts')
      .select('*')
      .eq('event_id', eventId)
      .order('sort_order');
    if (error) throw error;
    return (data ?? []) as WorkoutSummary[];
  });
}

export async function fetchHeats(workoutId: string): Promise<HeatSummary[]> {
  return withCache(`heats.${workoutId}`, async () => {
    const { data, error } = await supabase
      .from('heats')
      .select('id, name, status, scheduled_at, sort_order, started_at')
      .eq('workout_id', workoutId)
      .order('sort_order');
    if (error) throw error;
    return (data ?? []) as HeatSummary[];
  });
}

/**
 * Los carriles del heat, con el juez YA RESUELTO por la base.
 *
 * Antes se leia `heat_lanes.judge_id` a secas y `mine` se calculaba
 * aqui. Con arenas hay dos niveles --la excepcion del heat y la
 * asignacion fija del carril-- y resolverlos en la tablet significaria
 * que una version vieja de la app podria ensenarle a un juez carriles
 * que no son suyos, o esconderle los que si.
 *
 * Por eso `mine` lo decide `heat_roster` con auth.uid(): una sola
 * respuesta sobre quien juzga que, para todas las pantallas.
 */
export async function fetchLanes(heatId: string): Promise<LaneRow[]> {
  return withCache(`lanes.${heatId}`, async () => {
    const { data, error } = await supabase.rpc('heat_roster', { p_heat_id: heatId });
    if (error) throw error;

    return (data ?? []).map((row: any) => ({
      id: row.id,
      lane_number: row.lane_number,
      registration_id: row.registration_id,
      display_name: row.display_name,
      bib_number: row.bib_number,
      division_id: row.division_id,
      box_name: row.box_name,
      judge_id: row.judge_id,
      mine: Boolean(row.mine),
    })) as LaneRow[];
  });
}

/**
 * Los carriles que le tocan a este juez en el heat.
 *
 * Si no tiene ninguno asignado devuelve TODOS, a proposito. Un juez que
 * llega al pabellon y ve una lista vacia porque el organizador no acabo
 * de repartir carriles no puede trabajar, y la competencia se para. El
 * filtro se activa solo cuando alguien se ha molestado en asignar.
 */
export function myLanes(lanes: LaneRow[]): LaneRow[] {
  const mios = lanes.filter((l) => l.mine);
  return mios.length > 0 ? mios : lanes;
}

/** Si hay reparto de carriles en este heat y a este juez le toca solo una parte. */
export function isFiltered(lanes: LaneRow[]): boolean {
  return lanes.some((l) => l.mine) && lanes.some((l) => !l.mine);
}

export async function fetchScores(workoutId: string): Promise<ScoreRow[]> {
  return withCache(`scores.${workoutId}`, async () => {
    const { data, error } = await supabase
      .from('scores')
      .select(
        'id, workout_id, registration_id, status, time_seconds, reps, load_kg, distance_m, capped, tiebreak_seconds, notes, segments_done, partial_reps',
      )
      .eq('workout_id', workoutId);
    if (error) throw error;
    return (data ?? []) as ScoreRow[];
  });
}


/* ---------------------------------------------------------------------
 * Cronometro del heat
 * -------------------------------------------------------------------*/

/**
 * Desfase entre el reloj del servidor y el de esta tablet.
 *
 * Se mide una vez al arrancar. Una tablet con el reloj adelantado dos
 * minutos pintaria el cronometro adelantado dos minutos, y dos jueces del
 * mismo heat cantarian el time cap con dos minutos de diferencia.
 */
export async function clockOffsetMs(): Promise<number> {
  const antes = Date.now();
  const { data, error } = await supabase.rpc('server_now');
  if (error || !data) return 0;
  const despues = Date.now();
  // La mitad del viaje de ida y vuelta: es la mejor estimacion del
  // instante en que el servidor respondio.
  const local = antes + (despues - antes) / 2;
  return new Date(data as string).getTime() - local;
}

export async function startHeat(heatId: string): Promise<void> {
  const { error } = await supabase.rpc('start_heat', { p_heat_id: heatId });
  if (error) throw new Error(error.message);
}

export async function stopHeat(heatId: string): Promise<void> {
  const { error } = await supabase.rpc('stop_heat', { p_heat_id: heatId });
  if (error) throw new Error(error.message);
}

export async function resetHeat(heatId: string): Promise<void> {
  const { error } = await supabase.rpc('reset_heat', { p_heat_id: heatId });
  if (error) throw new Error(error.message);
}

/** Si este evento quiere cronometro, y si yo puedo arrancarlo. */
export async function fetchTimerContext(
  eventId: string,
): Promise<{ enabled: boolean; canControl: boolean }> {
  const uid = await miId();

  // Aqui el filtro no era cosmetico: sin el, a un admin le llegaban las
  // filas de todo el equipo y `maybeSingle()` falla cuando hay mas de
  // una. El rol salia vacio y el organizador PERDIA el control del heat
  // justo por tener jueces invitados.
  const [ev, staff] = await Promise.all([
    supabase.from('events').select('judge_timer').eq('id', eventId).maybeSingle(),
    supabase
      .from('event_staff')
      .select('role')
      .eq('event_id', eventId)
      .eq('user_id', uid ?? '')
      .maybeSingle(),
  ]);
  const role = (staff.data as { role?: string } | null)?.role ?? '';
  return {
    enabled: Boolean((ev.data as { judge_timer?: boolean } | null)?.judge_timer),
    canControl: ['owner', 'admin', 'head_judge', 'scorekeeper'].includes(role),
  };
}
